import React from 'react';
import { ArrowUpRightIcon, CheckIcon, TargetIcon } from 'lucide-react';
import { ScorecardResult, Tier } from '../types/commission';
import { activeStep, formatCurrency, nextStep } from '../utils/commission';
interface UnitBonusTrackerProps {
  tier: Tier;
  units: number;
  volume: number;
  scorecard: ScorecardResult;
  eligible: boolean;
  isCurrent: boolean;
}
export function UnitBonusTracker({
  tier,
  units,
  volume,
  scorecard,
  eligible,
  isCurrent
}: UnitBonusTrackerProps) {
  const current = activeStep(tier, units);
  const next = nextStep(tier, units);
  const topStep = tier.steps[tier.steps.length - 1];
  const scorecardMet = scorecard.points >= scorecard.goal;
  const bonusBps = eligible ? current.bonusBps : 0;
  const nextValue = next ? volume * (next.bps - current.bps) / 10000 : 0;
  return <section aria-labelledby="unit-bonus-heading" className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-card" data-id="element-119" data-mp="3txifmqsa60xx">
      <div data-id="element-120" data-mp="2lp8b4gpjryv">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-700" data-id="element-121" data-mp="33zgukdpazbag">Unit bonus</p>
        <h2 id="unit-bonus-heading" className="mt-1.5 text-2xl font-bold text-slate-900" data-id="element-122" data-mp="2z0mbdanfbkpt">
          {bonusBps > 0 ? `+${bonusBps} BPS earned` : scorecardMet ? 'No bonus yet' : 'Bonus not earned'}
        </h2>
      </div>

      <div className="mt-6" data-id="element-123" data-mp="380sk6j86fwin">
        <div className="h-2 overflow-hidden rounded-full bg-slate-100" data-id="element-124" data-mp="37zhiqq27f0up">
          <div role="progressbar" aria-label="Funded units toward the top unit bonus" aria-valuenow={units} aria-valuemin={0} aria-valuemax={topStep.units} className="h-full rounded-full bg-brand-600" style={{
          width: `${Math.min(100, units / topStep.units * 100)}%`
        }} data-id="element-125" data-mp="3mp5j9p2k9vhd" />
        </div>

        <ol className="mt-4 grid grid-cols-4 gap-3" data-id="element-126" data-mp="3rq30nzfqylht">
          {tier.steps.map(step => {
          const reached = eligible && units >= step.units;
          const isActiveStep = eligible && step.units === current.units;
          return <li key={step.units} className={`rounded-lg border px-3 py-2.5 ${isActiveStep ? 'border-brand-600 bg-brand-50' : 'border-slate-200 bg-white'}`} data-id="element-127" data-mp="1xctmhuc9abt8">
                <div className="flex items-center justify-between gap-2" data-id="element-128" data-mp="58nytvz55m92">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-129" data-mp="a8k5y434xtsu">
                    {step.units === 0 ? 'Base' : `${step.units}+`}
                  </span>
                  {reached ? <CheckIcon className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" data-id="element-130" data-mp="j98gupx8fe1i" /> : null}
                </div>
                <p className={`mt-1 text-lg font-bold tabular-nums ${reached ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-131" data-mp="o7jpwyfpdkio">
                  {step.bps}
                  <span className="ml-1 text-xs font-semibold text-slate-400" data-id="element-132" data-mp="t14rf0gi8wmm">BPS</span>
                </p>
              </li>;
        })}
        </ol>
      </div>

      <div className="mt-6 flex items-start gap-2.5 rounded-lg bg-slate-50 px-4 py-3" data-id="element-133" data-mp="12y96kgbxnrtx">
        <TargetIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-134" data-mp="16y1i91wmie3i" />
        <p className="text-sm text-slate-700" data-id="element-135" data-mp="2rhdcv031qsng">
          {!scorecardMet ? <>
              Scorecard goal missed — bonus does not apply this period.
            </> : next && isCurrent ? <>
              <span className="font-semibold text-slate-900" data-id="element-136" data-mp="2ifnkns1cbpom">{next.units - units} more funded units</span> unlocks{' '}
              {next.bps} BPS, worth about{' '}
              <span className="font-semibold tabular-nums text-slate-900" data-id="element-137" data-mp="2dj6uksakz18w">{formatCurrency(nextValue)}</span> more.
            </> : next ? <>Closed {next.units - units} units short of the {next.bps} BPS step.</> : <>Top step reached — every funded loan pays {current.bps} BPS.</>}
        </p>
      </div>

      <dl className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-200 pt-4 text-sm" data-id="element-138" data-mp="31cym9jag2v78">
        <div className="flex items-center gap-2" data-id="element-139" data-mp="3tvc7mjelgopf">
          <dt className="text-slate-500" data-id="element-140" data-mp="3tw43lvc3sog8">Scorecard points</dt>
          <dd className={`font-semibold tabular-nums ${scorecardMet ? 'text-slate-900' : 'text-red-600'}`} data-id="element-141" data-mp="3p4vu6zarx332">
            {scorecard.points.toLocaleString()}
            <span className="font-medium text-slate-400" data-id="element-142" data-mp="39lb3bemysw5e"> / {scorecard.goal.toLocaleString()}</span>
          </dd>
        </div>
        <a href="#/scorecard" onClick={event => event.preventDefault()} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-brand-700 transition hover:text-brand-900" data-id="element-143" data-mp="2zoes315olvx5">
          Scorecard details
          <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-144" data-mp="3sckertgapuzm" />
        </a>
      </dl>
    </section>;
}